import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

// Type for the feature flag map (flag -> enabled?)
export type FeatureFlagMap = Record<string, boolean>;

// Parse the FeatureFlags cookie (if present) and return the map
function parseFeatureCookie(): FeatureFlagMap {
  const cookiePair = document.cookie
    .split('; ')
    .find((c) => c.startsWith('FeatureFlags='));

  if (!cookiePair) return {};

  try {
    const raw = cookiePair.split('=')[1];
    return JSON.parse(decodeURIComponent(raw));
  } catch {
    // Malformed cookie – ignore
    return {};
  }
}

const FeatureFlagContext = createContext<FeatureFlagMap>({});

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [flags, setFlags] = useState<FeatureFlagMap>(() => parseFeatureCookie());

  const refresh = useCallback(() => {
    setFlags(parseFeatureCookie());
  }, []);

  useEffect(() => {
    const hasCookieStore =
      typeof window !== 'undefined' &&
      'cookieStore' in window &&
      (window as any).cookieStore?.addEventListener;

    if (!hasCookieStore) {
      return; // No live updates—initial load only
    }

    const store: any = (window as any).cookieStore;

    const handler = (event: any) => {
      // event.changed is an array of cookies that changed
      if (event.changed?.some((c: any) => c.name === 'FeatureFlags')) {
        refresh();
      }
    };

    store.addEventListener('change', handler);
    return () => store.removeEventListener('change', handler);
  }, [refresh]);

  return (
    <FeatureFlagContext.Provider value={flags}>{children}</FeatureFlagContext.Provider>
  );
};

// Convenience hook to get the entire map
export function useFeatures(): FeatureFlagMap {
  return useContext(FeatureFlagContext);
}

